import React, { createContext, useContext, useState, ReactNode } from 'react';
import { DashboardFilterStore } from '../../types/dashboards/dashboard-filter-store';
import { Chart, TChart } from '../../api/charts/Chart';
import { TDataframe, TReport } from '../../api/types';
import { Dashboard, TDashboard } from '../../api/dashbboards/Dashboards';
import { Present, TPresent } from '../../api/present/Present';
import { StatSummary } from '../../api/types/lambda-graph-summaries-request';

interface CommonStoreContext {
	sharedDashboardFilters: DashboardFilterStore[];
	setSharedDashboardFilters: (filters: DashboardFilterStore[]) => void;
	currentSelectedChart: TChart;
	setCurrentSelectedChart: (chart: TChart) => void;
	currentSelectedDataframe: TDataframe | undefined;
	setCurrentSelectedDataframe: (dataframe: TDataframe | undefined) => void;
	currentSelectedTableReport: TReport | undefined;
	setCurrentSelectedTableReport: (entity: TReport | undefined) => void;
	currentSelectedDashboard: TDashboard;
	setCurrentSelectedDashboard: (dashboard: TDashboard) => void;
	currentSelectedPresentation: TPresent;
	setCurrentSelectedPresentation: (present: TPresent) => void;
	currentGraphSummaryStats: StatSummary[];
	setCurrentGraphSummaryStats: React.Dispatch<React.SetStateAction<StatSummary[]>>;
	currentSelectedPresentSlide: number;
	setCurrentSelectedPresentSlide: React.Dispatch<React.SetStateAction<number>>;
	clickedNewSlide: boolean;
	setClickedNewSlide: React.Dispatch<React.SetStateAction<boolean>>;
	currentFilterName: string;
	setCurrentFilterName: React.Dispatch<React.SetStateAction<string>>;
	aiSuggestedQuestions: string[] | undefined;
	setAiSuggestedQuestions: React.Dispatch<React.SetStateAction<string[] | undefined>>;
}

const CommonStoreContext = createContext<CommonStoreContext | null>(null);

export const CommonStoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [sharedDashboardFilters, setSharedDashboardFilters] = useState<DashboardFilterStore[]>(
		[]
	);
	const [currentSelectedChart, setCurrentSelectedChart] = useState<TChart>(Chart.Default());
	const [currentSelectedDataframe, setCurrentSelectedDataframe] = useState<TDataframe>();
	const [currentSelectedTableReport, setCurrentSelectedTableReport] = useState<TReport>();
	const [currentSelectedDashboard, setCurrentSelectedDashboard] = useState<TDashboard>(
		Dashboard.Default()
	);
	const [currentSelectedPresentation, setCurrentSelectedPresentation] = useState<TPresent>(
		Present.Default()
	);
	const [currentGraphSummaryStats, setCurrentGraphSummaryStats] = useState<StatSummary[]>(
		[] as StatSummary[]
	);
	const [currentSelectedPresentSlide, setCurrentSelectedPresentSlide] = useState(0);
	const [clickedNewSlide, setClickedNewSlide] = useState(false);
	const [currentFilterName, setCurrentFilterName] = useState('');
	const [aiSuggestedQuestions, setAiSuggestedQuestions] = useState<string[] | undefined>(
		undefined
	);

	return (
		<CommonStoreContext.Provider
			value={{
				sharedDashboardFilters,
				setSharedDashboardFilters,
				currentSelectedChart,
				setCurrentSelectedChart,
				currentSelectedDataframe,
				setCurrentSelectedDataframe,
				currentSelectedTableReport,
				setCurrentSelectedTableReport,
				currentSelectedDashboard,
				setCurrentSelectedDashboard,
				currentSelectedPresentation,
				setCurrentSelectedPresentation,
				currentGraphSummaryStats,
				setCurrentGraphSummaryStats,
				currentSelectedPresentSlide,
				setCurrentSelectedPresentSlide,
				clickedNewSlide,
				setClickedNewSlide,
				currentFilterName,
				setCurrentFilterName,
				aiSuggestedQuestions,
				setAiSuggestedQuestions,
			}}
		>
			{children}
		</CommonStoreContext.Provider>
	);
};

export const useCommonStoreContext = () => {
	const context = useContext(CommonStoreContext);
	if (!context)
		throw new Error('useCommonStoreContext must be used within a CommonStoreProvider');
	return context;
};
